import { createFileRoute } from '@tanstack/react-router'
import { format } from 'date-fns'
import { id } from 'date-fns/locale'
import { Download, TrendingDown, TrendingUp } from 'lucide-react'
import { useState } from 'react'

import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import { toIdr } from '@/libs/utils/format'

export const Route = createFileRoute('/_app/report/income-statement')({
  loader: () => {
    return {
      crumb: 'Laporan Laba Rugi',
    }
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <PageLayout>
      <PageLayout.Header title='Laporan Laba Rugi' />
      <IncomeStatementPage />
    </PageLayout>
  )
}

interface IncomeItem {
  code: string
  name: string
  amount: number
  level: number
  isHeader: boolean
  isNegative?: boolean
}

interface IncomeStatementData {
  revenue: IncomeItem[]
  cogs: IncomeItem[]
  operatingExpenses: IncomeItem[]
  nonOperating: IncomeItem[]
  totalRevenue: number
  totalCOGS: number
  grossProfit: number
  totalOperatingExpenses: number
  operatingProfit: number
  netProfit: number
  grossProfitMargin: number
  netProfitMargin: number
}

const OUTLETS = [
  { id: 'ikki_coffee', name: 'Ikki Coffee' },
  { id: 'ikki_resto', name: 'Ikki Resto' },
]

const mockData: Record<string, IncomeStatementData> = {
  ikki_coffee: {
    revenue: [
      { code: '40000', name: 'PENDAPATAN USAHA', amount: 0, level: 1, isHeader: true },
      { code: '40100', name: 'Penjualan Tunai', amount: 45250000, level: 2, isHeader: false },
      {
        code: '40300',
        name: 'Diskon Penjualan',
        amount: -1250000,
        level: 2,
        isHeader: false,
        isNegative: true,
      },
    ],
    cogs: [
      { code: '50000', name: 'HARGA POKOK PENJUALAN', amount: 0, level: 1, isHeader: true },
      { code: '50010', name: 'HPP (Bahan Baku)', amount: 18500000, level: 2, isHeader: false },
      { code: '50200', name: 'Pembelian Bahan Baku', amount: 9200000, level: 2, isHeader: false },
    ],
    operatingExpenses: [
      { code: '60000', name: 'BIAYA OPERASIONAL', amount: 0, level: 1, isHeader: true },
      { code: '60100', name: 'Gaji Karyawan', amount: 8500000, level: 2, isHeader: false },
      { code: '60120', name: 'BPJS & Tunjangan', amount: 1200000, level: 2, isHeader: false },
      { code: '60200', name: 'Listrik & Air', amount: 1800000, level: 2, isHeader: false },
      { code: '61300', name: 'Penyusutan', amount: 750000, level: 2, isHeader: false },
    ],
    nonOperating: [
      { code: '70000', name: 'PENDAPATAN/BIAYA LAIN', amount: 0, level: 1, isHeader: true },
      { code: '70100', name: 'Pendapatan Lain', amount: 500000, level: 2, isHeader: false },
    ],
    totalRevenue: 45250000 - 1250000,
    totalCOGS: 18500000 + 9200000,
    grossProfit: 45250000 - 1250000 - (18500000 + 9200000),
    totalOperatingExpenses: 8500000 + 1200000 + 1800000 + 750000,
    operatingProfit: 0,
    netProfit: 0,
    grossProfitMargin: 0,
    netProfitMargin: 0,
  },
  ikki_resto: {
    revenue: [
      { code: '40000', name: 'PENDAPATAN USAHA', amount: 0, level: 1, isHeader: true },
      { code: '40100', name: 'Penjualan Tunai', amount: 68200000, level: 2, isHeader: false },
      {
        code: '40300',
        name: 'Diskon Penjualan',
        amount: -1800000,
        level: 2,
        isHeader: false,
        isNegative: true,
      },
    ],
    cogs: [
      { code: '50000', name: 'HARGA POKOK PENJUALAN', amount: 0, level: 1, isHeader: true },
      { code: '50010', name: 'HPP (Bahan Baku)', amount: 28500000, level: 2, isHeader: false },
      { code: '50200', name: 'Pembelian Bahan Baku', amount: 14200000, level: 2, isHeader: false },
    ],
    operatingExpenses: [
      { code: '60000', name: 'BIAYA OPERASIONAL', amount: 0, level: 1, isHeader: true },
      { code: '60100', name: 'Gaji Karyawan', amount: 12500000, level: 2, isHeader: false },
      { code: '60120', name: 'BPJS & Tunjangan', amount: 1800000, level: 2, isHeader: false },
      { code: '60200', name: 'Listrik & Air', amount: 2200000, level: 2, isHeader: false },
      { code: '61300', name: 'Penyusutan', amount: 1130000, level: 2, isHeader: false },
    ],
    nonOperating: [
      { code: '70000', name: 'PENDAPATAN/BIAYA LAIN', amount: 0, level: 1, isHeader: true },
      { code: '70100', name: 'Pendapatan Lain', amount: 800000, level: 2, isHeader: false },
    ],
    totalRevenue: 68200000 - 1800000,
    totalCOGS: 28500000 + 14200000,
    grossProfit: 68200000 - 1800000 - (28500000 + 14200000),
    totalOperatingExpenses: 12500000 + 1800000 + 2200000 + 1130000,
    operatingProfit: 0,
    netProfit: 0,
    grossProfitMargin: 0,
    netProfitMargin: 0,
  },
}

// Hitung laba
Object.keys(mockData).forEach(outlet => {
  const d = mockData[outlet]
  d.operatingProfit = d.grossProfit - d.totalOperatingExpenses
  d.netProfit = d.operatingProfit + 500000 // + pendapatan lain
  d.grossProfitMargin = d.totalRevenue > 0 ? (d.grossProfit / d.totalRevenue) * 100 : 0
  d.netProfitMargin = d.totalRevenue > 0 ? (d.netProfit / d.totalRevenue) * 100 : 0
})

function IncomeStatementPage() {
  const [outletId, setOutletId] = useState('ikki_coffee')
  const [period] = useState('2025-11')

  const data = mockData[outletId]

  const renderSection = (title: string, items: IncomeItem[], total: number, isNegative = false) => (
    <Card className='mb-6'>
      <CardHeader>
        <CardTitle className='text-lg font-semibold'>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-full'>Akun</TableHead>
              <TableHead className='text-right'>Jumlah</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map(item => (
              <TableRow
                key={item.code}
                className={
                  item.isHeader
                    ? `
                      bg-gray-50 font-bold
                      dark:bg-gray-800
                    `
                    : item.isNegative
                      ? 'text-red-600'
                      : ''
                }
              >
                <TableCell style={{ paddingLeft: `${item.level * 24}px` }}>
                  {item.isHeader ? item.name : `${item.code} - ${item.name}`}
                </TableCell>
                <TableCell className='text-right font-medium'>
                  {item.isNegative ? '(' : ''}
                  {toIdr(Math.abs(item.amount))}
                  {item.isNegative ? ')' : ''}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className='border-t-2 border-gray-300 font-bold'>
              <TableCell>Total {title}</TableCell>
              <TableCell
                className={`
                  text-right
                  ${isNegative ? 'text-red-600' : ''}
                `}
              >
                {isNegative && total > 0 ? '(' : ''}
                {toIdr(Math.abs(total))}
                {isNegative && total > 0 ? ')' : ''}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )

  return (
    <div>
      {/* Header */}
      <div
        className={`
          mb-6 rounded-lg bg-white p-6 shadow-sm
          dark:bg-gray-800
        `}
      >
        <div
          className={`
            flex flex-col items-start justify-between gap-4
            md:flex-row md:items-center
          `}
        >
          <div>
            <h1
              className={`
                text-2xl font-bold text-gray-900
                dark:text-white
              `}
            >
              Laporan Laba Rugi
            </h1>
            <p
              className={`
                mt-1 text-sm text-gray-500
                dark:text-gray-400
              `}
            >
              Kinerja keuangan per outlet
            </p>
          </div>
          <div className='flex gap-3'>
            <Button variant='outline' size='sm'>
              <Download className='mr-2 h-4 w-4' />
              Export PDF
            </Button>
            <Button variant='outline' size='sm'>
              <Download className='mr-2 h-4 w-4' />
              Export Excel
            </Button>
          </div>
        </div>

        <div
          className={`
            mt-6 grid grid-cols-1 gap-4
            md:grid-cols-2
          `}
        >
          <div>
            <Label htmlFor='outlet'>Outlet</Label>
            <Select value={outletId} onValueChange={setOutletId}>
              <SelectTrigger id='outlet'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {OUTLETS.map(o => (
                  <SelectItem key={o.id} value={o.id}>
                    {o.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Periode</Label>
            <div
              className={`
                flex items-center gap-2 text-sm font-medium text-gray-700
                dark:text-gray-300
              `}
            >
              <span>{format(new Date(period + '-01'), 'MMMM yyyy', { locale: id })}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Laporan */}
      {renderSection('PENDAPATAN USAHA', data.revenue, data.totalRevenue)}
      {renderSection('HARGA POKOK PENJUALAN', data.cogs, data.totalCOGS, true)}

      {/* Laba Kotor */}
      <Card
        className={`
          mb-6 border-green-200
          dark:border-green-800
        `}
      >
        <CardContent className='pt-6'>
          <div className='flex items-center justify-between text-lg font-bold'>
            <span className='flex items-center gap-2'>
              <TrendingUp className='h-5 w-5 text-green-600' />
              Laba Kotor
            </span>
            <span
              className={`
                text-green-600
                dark:text-green-400
              `}
            >
              {toIdr(data.grossProfit)} ({data.grossProfitMargin.toFixed(1)}%)
            </span>
          </div>
        </CardContent>
      </Card>

      {renderSection(
        'BIAYA OPERASIONAL',
        data.operatingExpenses,
        data.totalOperatingExpenses,
        true
      )}

      {/* Laba Operasional */}
      <Card
        className={`
          mb-6 border-blue-200
          dark:border-blue-800
        `}
      >
        <CardContent className='pt-6'>
          <div className='flex items-center justify-between text-lg font-bold'>
            <span>Laba Operasional</span>
            <span
              className={`
                text-blue-600
                dark:text-blue-400
              `}
            >
              {toIdr(data.operatingProfit)}
            </span>
          </div>
        </CardContent>
      </Card>

      {renderSection('PENDAPATAN/BIAYA LAIN', data.nonOperating, 500000)}

      {/* Laba Bersih */}
      <Card
        className={`
          border-purple-200
          dark:border-purple-800
        `}
      >
        <CardContent className='pt-6'>
          <div className='flex items-center justify-between text-xl font-bold'>
            <span className='flex items-center gap-2'>
              <TrendingUp className='h-6 w-6 text-purple-600' />
              LABA BERSIH
            </span>
            <span
              className={`
                text-purple-600
                dark:text-purple-400
              `}
            >
              {toIdr(data.netProfit)} ({data.netProfitMargin.toFixed(1)}%)
            </span>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
